import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';

/**
 * User detail — the facts, the links out, and six actions.
 *
 * All six the old page has, with the design's own dialog copy for the four it
 * wrote specs for (change email, generate password, forget user, set balance).
 * Two of them are the reason the dialog exists at all:
 *
 *   - **Forget user** anonymises irreversibly and is a GDPR action, so it is
 *     `danger` and says what survives (bookings and transactions, anonymised)
 *     rather than only what goes.
 *   - **Generate password** invalidates the current one immediately. The API
 *     generates the new one and returns it only once it is saved; it is then
 *     shown in a dialog with a copy button, because a generated password nobody
 *     captured is an account nobody can get into.
 *
 * **Change email carries a caveat the old page shows as an `alert`**: the admin
 * cache at admin.wannasport.dk has to be cleared afterwards or the old address
 * lingers. That is not an aside — it is the difference between the change taking
 * effect and appearing to. It moves into the dialog's warning strip, where it is
 * read before the action rather than after.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');
const fmtAmount = value => (value == null ? '—' : Number(value).toFixed(2));

// How many wallet changes the page shows; older ones are rare enough that the
// page does not paginate them.
const WALLET_HISTORY_COUNT = 50;

const UserInternal = ({ api }) => {
  const { userId } = useParams();
  const { ask, modalProps } = useModal();

  const [user, setUser] = useState(null);
  const [providerAdminId, setProviderAdminId] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [walletHistory, setWalletHistory] = useState(null);

  const load = useCallback(async () => {
    try {
      setUser(await api.users.getUser(userId));
      setFailed(false);
    } catch (error) {
      setFailed(true);
      return;
    }

    try {
      const admin = await api.providerAdmin.getProviderAdminIdByUser(userId);
      setProviderAdminId(admin?.success ? admin.data : null);
    } catch (error) {
      // Not being a provider admin is the normal case, and the endpoint answers
      // unevenly for it; treated as "no".
      setProviderAdminId(null);
    }

    try {
      setWalletHistory(await api.users.getWalletHistory(userId, 1, WALLET_HISTORY_COUNT));
    } catch (error) {
      // The rest of the page works without it; the card says it failed.
      setWalletHistory({ error: error.message });
    }
  }, [api, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const say = (tone, text) => setNote({ tone, text });
  const ctx = `user …${String(userId).slice(-6)}`;

  const changeEmail = () => ask({
    kind: 'input',
    title: 'Change the user’s email',
    context: `Change email · ${ctx}`,
    body: 'Future mail goes to the new address. History is unchanged.',
    label: 'New email',
    placeholder: 'name@domain.dk',
    warning: 'Clear the cache in the maintenance section of admin.wannasport.dk afterwards, or the old address keeps being used.',
    confirmLabel: 'Update email',
    onConfirm: async email => {
      await api.users.changeEmail(email.trim(), userId);
      say('info', `Email changed to ${email.trim()}. Remember to clear the admin cache.`);
      await load();
    }
  });

  const generateNewPassword = () => ask({
    kind: 'danger',
    title: 'Generate a new password?',
    context: `Generate password · ${ctx}`,
    body: 'A new password is generated and shown to you. The current one stops working immediately.',
    warning: 'The user is not notified. You have to pass it on yourself.',
    confirmLabel: 'Generate',
    onConfirm: async () => {
      try {
        setGeneratedPassword(await api.users.generatePassword(userId));
      } catch (error) {
        say('error', `Unable to generate a new password: ${error.message}`);
        throw error;
      }
    }
  });

  const forgetUser = () => ask({
    kind: 'danger',
    title: 'Anonymise this user?',
    context: `Forget user · ${ctx}`,
    body: 'Name, email and phone are removed for good. Bookings and transactions stay, with anonymised details.',
    warning: 'A GDPR action, not reversible.',
    confirmLabel: 'Anonymise user',
    onConfirm: async () => {
      await api.users.forgetUser(userId);
      say('info', 'User anonymised.');
      await load();
    }
  });

  const setBalance = () => ask({
    kind: 'input',
    title: 'Set the balance',
    context: `Set balance · ${ctx}`,
    body: 'The new balance replaces the current one and is recorded as a manual adjustment.',
    label: 'Balance (DKK)',
    placeholder: '0.00',
    hint: user?.Balance != null ? `Currently ${user.Balance} DKK` : undefined,
    mono: true,
    noteLabel: 'Reason / note (optional)',
    notePlaceholder: 'Shown in the wallet history',
    confirmLabel: 'Set balance',
    onConfirm: async (raw, reason) => {
      const amount = parseFloat(String(raw).replace(',', '.'));
      if (Number.isNaN(amount) || amount < 0) {
        say('error', 'That is not a valid amount.');
        throw new Error('invalid amount');
      }

      // The wallet has to exist before it can be set — same order as the old page.
      if (!user?.Wallet) {
        // `onlyPreview` is passed even though the function ignores it, because
        // `withApi` appends `language`, `timeZone` and the abort signal *after*
        // these arguments. Leaving it out does not make it `undefined` — it
        // shifts everything by one, so `timeZone` receives the AbortSignal and
        // the request goes out with an object as its `tz` header.
        await api.seasonalcourts.createWallet(userId, false);
      }
      try {
        await api.seasonalcourts.setWallet(userId, amount, reason || null);
      } catch (error) {
        say('error', `Unable to set the balance: ${error.message}`);
        throw error;
      }
      say('info', `Balance set to ${amount} DKK.`);
      await load();
    }
  });

  const addToProviderAdmin = () => ask({
    kind: 'confirm',
    title: 'Make this user a provider admin?',
    context: `Add to provider admin · ${ctx}`,
    body: 'They will be able to administer the providers and facilities assigned to them.',
    confirmLabel: 'Add as admin',
    onConfirm: async () => {
      const response = await api.providerAdmin.addUserToProviderAdmin(userId);
      const ok = response?.success !== false;
      say(ok ? 'info' : 'error', ok ? 'User is now a provider admin.' : 'Unable to add the user.');
      await load();
      if (!ok) throw new Error('add failed');
    }
  });

  const removeFromProviderAdmin = () => ask({
    kind: 'danger',
    title: 'Remove provider admin access?',
    context: `Remove as admin · ${ctx}`,
    body: 'They lose access to every provider and facility assigned to them.',
    confirmLabel: 'Remove access',
    onConfirm: async () => {
      const response = await api.providerAdmin.removeUserFromProviderAdmin(providerAdminId);
      const ok = response?.success !== false;
      say(ok ? 'info' : 'error', ok ? 'Access removed.' : 'Unable to remove the access.');
      await load();
      if (!ok) throw new Error('remove failed');
    }
  });

  if (failed) {
    return (
      <div className="next-page">
        <div className="next-note next-note--error">
          Could not load this user.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!user) return <div className="next-page next-muted">Loading…</div>;

  const fields = [
    ['Name', user.Name],
    ['Email', user.Email],
    ['Phone', user.PhoneNumber],
    ['Created at (CET)', fmtDate(user.CreatedAtCet || user.CreatedAt)],
    ['Password set', user.Password === true || user.HasPassword === true ? 'Yes' : 'No'],
    ['Connected accounts', Array.isArray(user.ConnectedAccounts)
      ? (user.ConnectedAccounts.join(', ') || 'None')
      : (user.ConnectedAccounts || 'None')],
    ['Balance', user.Balance != null ? `${user.Balance} DKK` : '—'],
    ['Provider admin', providerAdminId ? 'Yes' : 'No']
  ];

  // Everything this user touches, as searches already filtered to them. The five
  // that stay in this tree route; the two whose pages are still the old admin's
  // leave it.
  const links = [
    { label: 'Signups', hint: 'booked by this user', to: `/next/signups?user=${userId}` },
    { label: 'Purchases', hint: 'paid by this user', to: `/next/purchases?userId=${userId}` },
    { label: 'Subscriptions', hint: 'held by this user', to: `/next/subscriptions?userId=${userId}` },
    { label: 'Participants', hint: 'this user on a product', to: `/next/participants?userId=${userId}` },
    { label: 'Payment methods', hint: 'cards on file', to: `/next/payment-methods?userId=${userId}` },
    { label: 'Booking agreements', href: `/automated-booking-agreements?user=${userId}` },
    providerAdminId && { label: 'Provider admin', hint: 'this user administers a venue', href: `/provideradmin/${providerAdminId}` }
  ];

  const actions = [
    ['Change email', changeEmail],
    ['Set balance', setBalance],
    ['Generate password', generateNewPassword, 'danger'],
    ['Forget user', forgetUser, 'danger'],
    ...(providerAdminId
      ? [['Remove as provider admin', removeFromProviderAdmin, 'danger']]
      : [['Add to provider admin', addToProviderAdmin]])
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/users">← All users</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{user.Name || 'User'}</h1>
        </div>
        <span className="next-detail__id">{user.Id || userId}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>User</h2></div>
            <div className="next-facts next-facts--single">
              {fields.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">
                    {value === '' || value == null ? '—' : value}
                  </span>
                </div>
              ))}
              <div className="next-fact">
                <span className="next-fact__label">Intercom</span>
                <span className="next-fact__value">
                  {user.Email ? (
                    <a
                      className="next-link"
                      href={`https://app.intercom.com/a/apps/_/users/search?query=${encodeURIComponent(user.Email)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in Intercom
                    </a>
                  ) : '—'}
                </span>
              </div>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Wallet history</h2>
              <span className="next-muted">recorded since 27 Oct 2026</span>
            </div>
            {walletHistory === null && <p className="next-table__note next-muted">Loading…</p>}
            {walletHistory?.error && (
              <p className="next-table__note next-muted">Could not load the history: {walletHistory.error}</p>
            )}
            {Array.isArray(walletHistory) && walletHistory.length === 0 && (
              <p className="next-table__note next-muted">No balance change recorded.</p>
            )}
            {Array.isArray(walletHistory) && walletHistory.length > 0 && (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Date (CET)</th>
                      <th>Kind</th>
                      <th className="next-table__num">Previous → new</th>
                      <th className="next-table__num">Delta</th>
                      <th>Purchase</th>
                      <th>Admin</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walletHistory.map(change => (
                      <tr key={change.Id}>
                        <td data-label="Date (CET)">{fmtDate(change.CreatedAtCet)}</td>
                        <td data-label="Kind">{change.Kind}</td>
                        <td className="next-table__num" data-label="Previous → new">
                          {fmtAmount(change.PreviousAmount)} → {fmtAmount(change.NewAmount)}
                        </td>
                        <td
                          className="next-table__num"
                          data-label="Delta"
                          style={{ color: change.Delta > 0 ? 'var(--ws-success)' : (change.Delta < 0 ? 'var(--ws-danger)' : undefined) }}
                        >
                          {change.Delta > 0 ? '+' : ''}{fmtAmount(change.Delta)}
                        </td>
                        <td data-label="Purchase">
                          {change.PurchaseId
                            ? <Guid value={change.PurchaseId} to={`/next/purchase/${change.PurchaseId}`} />
                            : <span className="next-muted">—</span>}
                          {change.TransactionId && (
                            <div>
                              <Link className="next-link" to={`/next/transaction/${change.TransactionId}`}>transaction</Link>
                            </div>
                          )}
                        </td>
                        <td data-label="Admin">{change.AdminName || <span className="next-muted">—</span>}</td>
                        <td data-label="Note">{change.Note || <span className="next-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks items={links} />
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              {actions.map(([label, onClick, tone]) => (
                <button
                  type="button"
                  key={label}
                  className={`next-actionlist__item${tone === 'danger' ? ' next-actionlist__item--danger' : ''}`}
                  onClick={onClick}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        </aside>
      </div>

      <Modal {...modalProps} />
      <Modal
        open={!!generatedPassword}
        title="New password"
        context={`Generate password · ${ctx}`}
        body="The password has been saved. Copy it now: it is not shown again."
        copyValue={generatedPassword ?? ''}
        dismissOnly
        closeOnScrimClick={false}
        confirmLabel="Close"
        onConfirm={() => setGeneratedPassword(null)}
        onClose={() => setGeneratedPassword(null)}
      />
    </div>
  );
};

UserInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const User = withApi(UserInternal);

User.requiresLogin = true;

export default User;
